/**
 * Advanced Neural Noise Suppression Processor
 *
 * This processor implements the Option B architecture from the DFN3 thesis:
 * - AudioWorklet bridges audio to SharedArrayBuffer ring buffers
 * - dfn3-worker-v2 runs WASM DSP (STFT/ISTFT/ERB) + ONNX inference
 * - RNNoise WASM as adaptive fallback
 *
 * Architecture (from Phase 2 Documentation):
 * ┌─────────┐   ┌─────────────────┐   ┌─────────────────────────────────┐
 * │   Mic   │──►│ Bridge Worklet  │──►│         dfn3-worker-v2          │
 * │  Input  │   │ (Audio Thread)  │   │  ┌─────────┐    ┌───────────┐   │
 * └─────────┘   │                 │   │  │WASM DSP │───►│ONNX Model │   │
 *               │  inputSab ──────┼───┼──►│(STFT)  │    │ (8.4MB)   │   │
 *               │                 │   │  └─────────┘    └───────────┘   │
 *               │  outputSab ◄────┼───┼──┐                              │
 *               └─────────────────┘   │  │  ┌─────────┐                 │
 *                        │            │  └──│WASM DSP │◄────────────────┤
 *                        ▼            │     │(ISTFT)  │                 │
 *               ┌─────────────────┐   │     └─────────┘                 │
 *               │    Speaker      │   └─────────────────────────────────┘
 *               └─────────────────┘
 *
 * Performance Targets (from documentation):
 * - STFT Analysis: < 0.5ms
 * - ONNX Inference: < 6ms
 * - ISTFT Synthesis: < 0.5ms
 * - Total Latency: < 8ms (within 10ms hop budget)
 */

import { createSharedRingBuffer, type SharedRingBufferViews } from '../../../src/dfn3/dfn3-ring-buffer';
import { DFN3_HOP_SIZE, DFN3_SAMPLE_RATE } from '../../../src/dfn3/config';

export interface AdvancedNoiseProcessorConfig {
  sampleRate: number;
  enableVAD: boolean;
  vadProbabilityThreshold: number;
  vadHoldTime: number;
  aggressiveness: number;
}

export interface ProcessorMetrics {
  inputLevel: number;
  outputLevel: number;
  noiseReduction: number;
  vadProbability: number;
  cpuUsage: number;
  bufferHealth: number;
  latency: number;
}

type WorkletMode = 'dfn3' | 'rnnoise' | 'bypass';

export class AdvancedNoiseProcessor {
  private audioContext: AudioContext | null = null;
  private sourceNode: MediaStreamAudioSourceNode | null = null;
  private workletNode: AudioWorkletNode | null = null;
  private destinationNode: MediaStreamAudioDestinationNode | null = null;

  // DFN3 Worker (runs WASM DSP + ONNX inference)
  private dfn3Worker: Worker | null = null;
  private inputRing: SharedRingBufferViews | null = null;
  private outputRing: SharedRingBufferViews | null = null;

  private config: AdvancedNoiseProcessorConfig;
  private isProcessing = false;

  // Current mode
  private currentMode: WorkletMode = 'dfn3';
  private isUsingFallback = false;
  private onWorkletChange?: (worklet: string, isFallback: boolean) => void;

  // VAD
  private vad: any = null;
  private vadProbability = 0;
  private lastSpeechTime = 0;
  private isVADActive = false;

  // Metrics
  private metrics: ProcessorMetrics = {
    inputLevel: -Infinity,
    outputLevel: -Infinity,
    noiseReduction: 0,
    vadProbability: 0,
    cpuUsage: 0,
    bufferHealth: 0,
    latency: 0
  };

  private onMetricsUpdate?: (metrics: ProcessorMetrics) => void;
  private onVADStateChange?: (isActive: boolean) => void;

  // Ring buffer capacity (in samples) - ~500ms buffer
  private readonly RING_BUFFER_CAPACITY = DFN3_SAMPLE_RATE / 2; // 24000 samples

  constructor(config?: Partial<AdvancedNoiseProcessorConfig>) {
    this.config = {
      sampleRate: DFN3_SAMPLE_RATE,
      enableVAD: true,
      vadProbabilityThreshold: 0.5,
      vadHoldTime: 300,
      aggressiveness: 5,
      ...config
    };
  }

  /**
   * Initialize the processor using Option B architecture (dfn3-worker-v2)
   */
  async initialize(inputStream: MediaStream): Promise<MediaStream> {
    try {
      // Create audio context with ultra-low latency
      this.audioContext = new AudioContext({
        sampleRate: this.config.sampleRate,
        latencyHint: 0
      });

      console.log(`[AdvancedNoiseProcessor] AudioContext created:
        - Sample rate: ${this.audioContext.sampleRate}Hz
        - Base latency: ${(this.audioContext.baseLatency * 1000).toFixed(2)}ms
        - Output latency: ${(this.audioContext.outputLatency * 1000).toFixed(2)}ms
      `);

      // Step 1: Create SharedArrayBuffer ring buffers
      this.inputRing = createSharedRingBuffer(this.RING_BUFFER_CAPACITY);
      this.outputRing = createSharedRingBuffer(this.RING_BUFFER_CAPACITY);
      console.log('[AdvancedNoiseProcessor] SharedArrayBuffer ring buffers created');

      // Step 2: Spawn dfn3-worker-v2
      await this.initDfn3Worker();

      // Step 3: Load bridge worklet
      const cacheBuster = `?v=${Date.now()}`;
      await this.audioContext.audioWorklet.addModule(`/worklets/dfn3-bridge-worklet.js${cacheBuster}`);
      console.log('[AdvancedNoiseProcessor] Bridge worklet loaded');

      // Step 4: Create source from input stream
      this.sourceNode = this.audioContext.createMediaStreamSource(inputStream);

      // Step 5: Create AudioWorklet node with SharedArrayBuffer references
      this.workletNode = new AudioWorkletNode(
        this.audioContext,
        'dfn3-bridge-worklet',
        {
          numberOfInputs: 1,
          numberOfOutputs: 1,
          outputChannelCount: [1],
          processorOptions: {
            inputSab: this.inputRing.sab,
            outputSab: this.outputRing.sab,
            hopSize: DFN3_HOP_SIZE
          }
        }
      );

      // Handle worklet messages
      this.workletNode.port.onmessage = (event) => {
        const { type, data } = event.data;
        if (type === 'metrics') {
          this.metrics.bufferHealth = Math.min(100, (data.availableOutput / DFN3_HOP_SIZE) * 10);
        }
      };

      console.log('[AdvancedNoiseProcessor] Bridge worklet node created');

      // Step 6: Create destination
      this.destinationNode = this.audioContext.createMediaStreamDestination();

      // Step 7: Connect the audio graph
      this.sourceNode.connect(this.workletNode);
      this.workletNode.connect(this.destinationNode);

      // Initialize VAD if enabled
      if (this.config.enableVAD) {
        await this.initializeVAD();
      }

      this.isProcessing = true;
      this.onWorkletChange?.(this.currentMode, false);

      console.log('[AdvancedNoiseProcessor] Initialization complete, mode:', this.currentMode);

      return this.destinationNode.stream;
    } catch (error) {
      console.error('[AdvancedNoiseProcessor] Initialization failed:', error);
      throw error;
    }
  }

  /**
   * Initialize dfn3-worker-v2
   */
  private async initDfn3Worker(): Promise<void> {
    return new Promise((resolve, reject) => {
      // Create worker using inline worker import (Vite native worker support)
      // The ?worker suffix tells Vite to bundle this as a worker
      import('../../../src/dfn3/dfn3-worker-v2?worker').then((WorkerModule) => {
        this.dfn3Worker = new WorkerModule.default();
        this.setupWorkerHandlers(resolve, reject);
      }).catch(reject);
    });
  }

  private setupWorkerHandlers(resolve: () => void, reject: (err: Error) => void): void {
    if (!this.dfn3Worker) {
      reject(new Error('Worker not initialized'));
      return;
    }

    const timeout = setTimeout(() => {
      reject(new Error('DFN3 worker initialization timeout'));
    }, 30000); // 30s timeout for model loading + warmup

    this.dfn3Worker.onmessage = (event) => {
      const { type, data } = event.data;

      switch (type) {
        case 'ready':
          clearTimeout(timeout);
          console.log('[AdvancedNoiseProcessor] DFN3 worker ready:', data);
          this.currentMode = 'dfn3';
          this.isUsingFallback = false;
          resolve();
          break;

        case 'fallback':
          console.warn('[AdvancedNoiseProcessor] Worker fallback:', data.reason);
          this.currentMode = data.mode as WorkletMode;
          this.isUsingFallback = true;
          this.onWorkletChange?.(this.currentMode, true);
          // Still resolve - fallback is acceptable
          if (!this.isProcessing) {
            clearTimeout(timeout);
            resolve();
          }
          break;

        case 'metrics':
          this.metrics.cpuUsage = Math.min(100, (data.inferenceMs / 10) * 100);
          this.metrics.latency = data.inferenceMs + (this.audioContext?.baseLatency || 0) * 1000;
          this.onMetricsUpdate?.(this.metrics);
          break;

        case 'adaptive':
          console.log('[AdvancedNoiseProcessor] Adaptive mode:', data.level);
          break;

        case 'status':
          console.log('[AdvancedNoiseProcessor] Worker status:', data);
          break;

        case 'error':
          console.error('[AdvancedNoiseProcessor] Worker error:', data);
          break;
      }
    };

    this.dfn3Worker.onerror = (error) => {
      clearTimeout(timeout);
      console.error('[AdvancedNoiseProcessor] Worker error:', error);
      reject(error as unknown as Error);
    };

    // Send init message with SharedArrayBuffers
    this.dfn3Worker.postMessage({
      type: 'init',
      data: {
        inputSab: this.inputRing!.sab,
        outputSab: this.outputRing!.sab,
        hopSize: DFN3_HOP_SIZE,
        enableRnnoiseFallback: true,
        maxOverrunMs: 20
      }
    });

    console.log('[AdvancedNoiseProcessor] Init message sent to DFN3 worker');
  }

  /**
   * Initialize Silero VAD
   */
  private async initializeVAD(): Promise<void> {
    try {
      const [{ MicVAD }, ort] = await Promise.all([
        import('@ricky0123/vad-web'),
        import('onnxruntime-web')
      ]);

      ort.env.wasm.wasmPaths = '/vad/';

      // Type assertion needed - modelURL exists at runtime but not in type definitions
      this.vad = await MicVAD.new({
        modelURL: '/vad/silero_vad_legacy.onnx',
        ort: { wasmPaths: '/vad/' },
        onSpeechStart: () => {
          this.isVADActive = true;
          this.lastSpeechTime = Date.now();
          this.onVADStateChange?.(true);
        },
        onSpeechEnd: () => {
          setTimeout(() => {
            const timeSinceLastSpeech = Date.now() - this.lastSpeechTime;
            if (timeSinceLastSpeech >= this.config.vadHoldTime) {
              this.isVADActive = false;
              this.onVADStateChange?.(false);
            }
          }, this.config.vadHoldTime);
        },
        positiveSpeechThreshold: this.config.vadProbabilityThreshold,
        negativeSpeechThreshold: this.config.vadProbabilityThreshold - 0.15,
        preSpeechPadFrames: 1,
        redemptionFrames: 8,
        frameSamples: 1536,
        minSpeechFrames: 3,
        submitUserSpeechOnPause: false
      } as any);

      console.log('[AdvancedNoiseProcessor] Silero VAD initialized');
    } catch (error) {
      console.error('[AdvancedNoiseProcessor] VAD init failed:', error);
    }
  }

  /**
   * Start processing
   */
  start(): void {
    if (this.workletNode) {
      this.workletNode.port.postMessage({ type: 'start' });
    }
    this.vad?.start();
    this.isProcessing = true;
  }

  /**
   * Stop processing
   */
  stop(): void {
    if (this.workletNode) {
      this.workletNode.port.postMessage({ type: 'stop' });
    }
    this.vad?.pause();
    this.isProcessing = false;
  }

  /**
   * Toggle bypass mode
   */
  setBypass(enabled: boolean): void {
    if (this.workletNode) {
      this.workletNode.port.postMessage({ type: 'bypass', data: { enabled } });
    }
    if (this.dfn3Worker) {
      this.dfn3Worker.postMessage({ type: 'bypass' });
    }
  }

  /**
   * Reset processor state
   */
  reset(): void {
    if (this.workletNode) {
      this.workletNode.port.postMessage({ type: 'reset' });
    }
    if (this.dfn3Worker) {
      this.dfn3Worker.postMessage({ type: 'reset' });
    }
  }

  /**
   * Set attenuation strength (0.0 = none, 1.0 = normal, 2.0 = aggressive)
   */
  setAttenuation(strength: number): void {
    if (this.dfn3Worker) {
      this.dfn3Worker.postMessage({
        type: 'setAttenuation',
        data: { strength: Math.max(0, Math.min(2, strength)) }
      });
    }
  }

  /**
   * Set bass boost (1.0 = none, 1.5 = +50%, 3.0 = +200%)
   */
  setBassBoost(boost: number): void {
    if (this.dfn3Worker) {
      this.dfn3Worker.postMessage({
        type: 'setBassBoost',
        data: { boost: Math.max(0.5, Math.min(3, boost)) }
      });
    }
  }

  /**
   * Enable debug telemetry
   */
  setDebugMode(enabled: boolean, interval: number = 100): void {
    if (this.dfn3Worker) {
      this.dfn3Worker.postMessage({
        type: 'setDebugMode',
        data: { enabled, interval }
      });
    }
  }

  /**
   * Get current metrics
   */
  getMetrics(): ProcessorMetrics {
    return { ...this.metrics };
  }

  /**
   * Set callbacks
   */
  setOnMetricsUpdate(callback: (metrics: ProcessorMetrics) => void): void {
    this.onMetricsUpdate = callback;
  }

  setOnVADStateChange(callback: (isActive: boolean) => void): void {
    this.onVADStateChange = callback;
  }

  setOnWorkletChange(callback: (worklet: string, isFallback: boolean) => void): void {
    this.onWorkletChange = callback;
  }

  /**
   * Get VAD state
   */
  isVoiceActive(): boolean {
    return this.isVADActive;
  }

  /**
   * Get current worklet info
   */
  getCurrentWorklet(): { name: string; isFallback: boolean } {
    return {
      name: this.currentMode,
      isFallback: this.isUsingFallback
    };
  }

  /**
   * Attempt to recover to DFN3 mode
   */
  async tryRecoverDFN3(): Promise<boolean> {
    if (!this.isUsingFallback) {
      return true; // Already on DFN3
    }

    // Request worker to test if DFN3 can be recovered
    // The worker handles this internally via its adaptive system
    console.log('[AdvancedNoiseProcessor] DFN3 recovery requested');
    return false; // Worker will notify via onWorkletChange if recovery succeeds
  }

  /**
   * Cleanup and disconnect
   */
  async cleanup(): Promise<void> {
    this.stop();

    if (this.vad) {
      this.vad.destroy();
      this.vad = null;
    }

    if (this.dfn3Worker) {
      this.dfn3Worker.postMessage({ type: 'shutdown' });
      this.dfn3Worker.terminate();
      this.dfn3Worker = null;
    }

    if (this.workletNode) {
      this.workletNode.disconnect();
      this.workletNode = null;
    }

    if (this.sourceNode) {
      this.sourceNode.disconnect();
      this.sourceNode = null;
    }

    if (this.destinationNode) {
      this.destinationNode.disconnect();
      this.destinationNode = null;
    }

    if (this.audioContext) {
      await this.audioContext.close();
      this.audioContext = null;
    }

    this.inputRing = null;
    this.outputRing = null;
    this.isProcessing = false;

    console.log('[AdvancedNoiseProcessor] Cleanup complete');
  }

  /**
   * Get processing state
   */
  getIsProcessing(): boolean {
    return this.isProcessing;
  }

  /**
   * Get audio context for visualization
   */
  getAudioContext(): AudioContext | null {
    return this.audioContext;
  }

  /**
   * Get worklet node for direct connection
   */
  getWorkletNode(): AudioWorkletNode | null {
    return this.workletNode;
  }
}
